/**
 * src/hooks/usePlanGating.js
 *
 * Hook that provides plan-based access control for the current tenant.
 * Loads plan definitions once and caches them. Returns helpers to check
 * whether a module, feature, or limit is available on the tenant's plan.
 *
 * Usage:
 *   const { isModuleAllowed, isFeatureAllowed, getLimit, planDef, loading } = usePlanGating(tenant);
 *   if (!isModuleAllowed('threats')) { ... }
 */
import { useState, useEffect, useCallback } from 'react';
import { loadPlanDefinitionsResult } from '../api/planApi.js';
import { DEFAULT_PLAN_DEFINITIONS } from '../config/planDefaults.js';

// Module-level cache so multiple components share the same data.
let _cachedDefinitions = null;
let _cachePromise = null;
// When the cached definitions were fetched, so a long-lived tab can tell
// "confirmed a moment ago" from "confirmed when this page was opened".
let _cachedAt = 0;

// How long a fetched set of definitions is trusted before a mounted hook
// refreshes it in the background.
//
// #5654: the cache had no expiry and no way to reach an already-mounted
// component, so the frontend gate was only ever as fresh as the page load.
// A session lives 24 hours with the sliding refresh, so a plan changed by a
// super-admin — an upgrade or a downgrade — was invisible to every open tab
// until someone reloaded: a module the tenant had just lost stayed navigable
// (every action in it then failing server-side, where PLAN-B-001 is really
// enforced), and one they had just gained stayed hidden.
const CACHE_TTL_MS = 5 * 60_000;

// How often a mounted hook re-checks whether the cache has gone stale.
//
// #5654 (second pass). CACHE_TTL_MS above was only ever CONSULTED at two
// moments: when a hook mounts, and when the tab becomes visible again. But
// App.jsx calls usePlanGating() once at the root, so in a running SPA that
// hook never remounts — and a user working with the tab in front of them
// never fires visibilitychange either. The TTL therefore expired without
// anything ever looking at it, and the gate stayed exactly as fresh as the
// page load, which is the state this issue was opened about.
//
// So the staleness check is also driven on a timer while mounted. The
// interval is deliberately shorter than the TTL — it decides how often we
// ASK whether the cache is stale, while CACHE_TTL_MS still decides the
// answer, so a plan change is picked up within roughly the TTL rather than
// the TTL plus a whole interval.
const STALE_CHECK_INTERVAL_MS = 60_000;

// Every mounted usePlanGating, so a refresh reaches components that are
// already on screen rather than only the next one to mount. Without this,
// invalidatePlanCache() cleared the module cache and changed nothing a user
// could see, because each hook reads the cache once in a mount-only effect.
const subscribers = new Set();

function notifySubscribers() {
  for (const fn of [...subscribers]) {
    try {
      fn({ definitions: _cachedDefinitions, error: _cacheIsFromError });
    } catch {
      /* one bad subscriber must not stop the rest being told */
    }
  }
}
// True when the cache holds DEFAULT_PLAN_DEFINITIONS rather than definitions
// the server actually returned — because the fetch failed, OR because the
// server answered 200 with no `.definitions` payload at all.
//
// #5180: only the first of those two used to set this flag. The second is the
// commoner case (a tenant/environment whose plan definitions were never
// seeded) and it produced the more dangerous state: the hook reported
// `error: false`, so every caller was told the server had confirmed this
// tenant's plan when the server had said nothing about it. The distinction
// this flag exists to preserve is "did the server tell us this", and a 200
// with an empty body did not.
let _cacheIsFromError = false;

// A never-answering /api/plan-definitions must not pin the gate in "not known
// yet" forever. azureGet carries no request timeout of its own, so without this
// bound the initial load can simply never settle, and callers that refuse to
// decide until it does (guardModule, per PLAN-B-001) would hold a route
// indefinitely. The bound answers with the shipped defaults — a known baseline,
// flagged `error: true` because the server never confirmed it — and the real
// response still overrides it through notifySubscribers() whenever it lands.
const INITIAL_LOAD_TIMEOUT_MS = 10_000;

async function fetchDefinitions() {
  if (_cachedDefinitions) return { definitions: _cachedDefinitions, error: _cacheIsFromError };
  if (_cachePromise) return _cachePromise;

  const request = loadPlanDefinitionsResult()
    .then(({ definitions: defs, fromServer }) => {
      _cachedDefinitions = defs || DEFAULT_PLAN_DEFINITIONS;
      _cacheIsFromError = !fromServer;
    })
    .catch((e) => {
      // Module/feature gating is enforced server-side regardless of this UI
      // hint (PLAN-B-001) — a fetch failure only affects what the tenant SEES
      // as available, so it's safe to render from a fallback. But the fetch
      // genuinely failed, so callers get told via `error` rather than this
      // silently looking identical to "server confirmed the standard plan".
      console.error('[usePlanGating] loadPlanDefinitions failed:', e.message);
      _cachedDefinitions = DEFAULT_PLAN_DEFINITIONS;
      _cacheIsFromError = true;
    })
    .then(() => {
      _cachedAt = Date.now();
      _cachePromise = null;
      // Tell every mounted hook, not just whoever is awaiting this promise:
      // when the timeout below answered first, the awaiting caller has already
      // moved on with the defaults and this is the only way the authoritative
      // answer reaches the gate.
      notifySubscribers();
      return { definitions: _cachedDefinitions, error: _cacheIsFromError };
    });

  // Bound only what the caller WAITS for. `request` keeps running either way.
  _cachePromise = Promise.race([
    request,
    new Promise((resolve) => {
      setTimeout(
        () => resolve({ definitions: DEFAULT_PLAN_DEFINITIONS, error: true }),
        INITIAL_LOAD_TIMEOUT_MS
      );
    }),
  ]);

  return _cachePromise;
}

/**
 * Re-fetch definitions the tab already holds, and tell every mounted hook.
 *
 * Deliberately NOT fetchDefinitions(): that one falls back to
 * DEFAULT_PLAN_DEFINITIONS when the request fails, which is the right answer
 * when we have nothing at all, and the wrong one here. Overwriting definitions
 * the server confirmed with shipped defaults because of a transient network
 * blip could hide a module the tenant genuinely has — a refresh must never
 * make the answer worse than not refreshing. So a failed refresh keeps what we
 * had, and the next one tries again.
 */
function refreshDefinitions() {
  if (_cachePromise) return _cachePromise;
  // Stamp now, not on completion: an endpoint that is failing must not be
  // re-hit by every mounted hook on every render while it stays down.
  _cachedAt = Date.now();
  return loadPlanDefinitionsResult()
    .then(({ definitions: defs, fromServer }) => {
      if (!defs || !fromServer) return;
      _cachedDefinitions = defs;
      _cacheIsFromError = false;
      notifySubscribers();
    })
    .catch((e) => {
      console.error('[usePlanGating] plan definition refresh failed:', e.message);
    });
}

function cacheIsStale() {
  return !_cachedAt || Date.now() - _cachedAt > CACHE_TTL_MS;
}

/**
 * @param {object|null} tenant — tenant record from useTenant(), must have `.plan` field
 */
export function usePlanGating(tenant) {
  const [definitions, setDefinitions] = useState(_cachedDefinitions);
  const [loading, setLoading] = useState(!_cachedDefinitions);
  const [error, setError] = useState(_cacheIsFromError);

  useEffect(() => {
    let alive = true;
    const apply = ({ definitions: defs, error: err }) => {
      if (!alive) return;
      setDefinitions(defs);
      setError(err);
      setLoading(false);
    };
    // Stay subscribed for the lifetime of the component, so a refresh or an
    // explicit invalidation re-evaluates this gate instead of waiting for the
    // next mount (#5654).
    subscribers.add(apply);

    if (_cachedDefinitions) {
      apply({ definitions: _cachedDefinitions, error: _cacheIsFromError });
      // Trusted, but not forever — see CACHE_TTL_MS.
      if (cacheIsStale()) refreshDefinitions();
    } else {
      fetchDefinitions().then(apply);
    }

    // Coming back to the tab is the moment a plan change is most likely to
    // have happened since we last looked, and the cheapest place to notice it.
    const refreshIfStale = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      if (cacheIsStale()) refreshDefinitions();
    };
    const onVisible = refreshIfStale;
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisible);
    }
    // The tab the user is actually sitting in front of fires no
    // visibilitychange and never remounts this hook — see
    // STALE_CHECK_INTERVAL_MS. refreshDefinitions() keeps whatever the server
    // last confirmed if the re-fetch fails, so a poll can only ever make the
    // gate more current, never less (PLAN-B-004).
    const staleTimer = setInterval(refreshIfStale, STALE_CHECK_INTERVAL_MS);

    return () => {
      alive = false;
      subscribers.delete(apply);
      clearInterval(staleTimer);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisible);
      }
    };
  }, []);

  const plan = tenant?.plan || 'standard';
  const planDef = definitions?.[plan] || definitions?.standard || DEFAULT_PLAN_DEFINITIONS.standard;
  // The plan definition this app ships for the same plan id. Used as the
  // fallback when the resolved definition carries no modules/features map of
  // its own — see the note on the guards below.
  const fallbackDef = DEFAULT_PLAN_DEFINITIONS[plan] || DEFAULT_PLAN_DEFINITIONS.standard;

  /**
   * Check if a module is allowed on this tenant's plan.
   *
   * #5180: this used to `return true` whenever the resolved plan definition
   * had no `modules` map — an unconditional open door for a definition the
   * server may never have confirmed. Server-side `checkPlanAllows` (PLAN-B-001)
   * is the real enforcement, so nothing was ever accessible that shouldn't be;
   * what it produced was a UI offering modules the tenant cannot use, which is
   * the wrong default direction for a fail-safe product. Fall back to the
   * shipped definition for the same plan instead of to "yes" — a known,
   * honest baseline rather than a blanket grant. `true` remains the last
   * resort only if that baseline is itself absent, where refusing everything
   * would black out the app over a missing constant.
   */
  const isModuleAllowed = useCallback(
    (moduleKey) => {
      const modules = planDef?.modules || fallbackDef?.modules;
      if (!modules) return true;
      // A saved definition written before a module existed has no key for it
      // (e.g. `workflows`, D8 = B). Absence there means "never decided", not
      // "allowed": fall back to the shipped definition for the same plan,
      // which is what the server's gate (shared/planGating.js) enforces.
      if (!(moduleKey in modules) && fallbackDef?.modules && moduleKey in fallbackDef.modules) {
        return fallbackDef.modules[moduleKey] !== false;
      }
      return modules[moduleKey] !== false;
    },
    [planDef, fallbackDef]
  );

  /** Check if a feature flag is enabled on this tenant's plan. Same fallback. */
  const isFeatureAllowed = useCallback(
    (featureKey) => {
      const features = planDef?.features || fallbackDef?.features;
      if (!features) return true;
      return features[featureKey] !== false;
    },
    [planDef, fallbackDef]
  );

  /**
   * Get a numeric limit for this plan.
   * Returns -1 for unlimited, 0 for not available, or the numeric cap.
   */
  const getLimit = useCallback(
    (limitKey) => {
      const limits = planDef?.limits || fallbackDef?.limits;
      if (!limits) return -1;
      const val = limits[limitKey];
      return val === undefined ? -1 : val;
    },
    [planDef, fallbackDef]
  );

  return { isModuleAllowed, isFeatureAllowed, getLimit, planDef, plan, loading, error };
}

/**
 * Force re-fetch of plan definitions (e.g. after super-admin saves changes).
 *
 * Clearing the module cache is not enough on its own: every mounted hook has
 * already read it in a mount-only effect, so before #5654 this changed nothing
 * anyone could see until the next navigation. Mounted hooks are told too.
 */
export function invalidatePlanCache() {
  _cachedDefinitions = null;
  _cachePromise = null;
  _cacheIsFromError = false;
  _cachedAt = 0;
  if (subscribers.size) {
    fetchDefinitions().then(({ definitions: defs, error: err }) => {
      notifySubscribers({ definitions: defs, error: err });
    });
  }
}
